import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { Label } from '../ui/label'
import {
  StudentDecision,
  VisibilityStatus,
} from '@/redux/slices/studentDecisionWebsocketSlice'
import { Badge } from '../ui/badge'
import { PaperAirplaneIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { Checkbox } from '../ui/Checkbox'
import OGEAvatar from '../OGE/OGEAvatar'
import { SERVER_URL } from '@/api/config'
import { useCallback, useMemo, useState } from 'react'
import {
  bulkAdjudicateDecisions,
  BulkAdjudicationData,
} from '@/redux/asyncThunks/studentDecisionWebsocket'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import ConfirmationModal from '../OGE/OGEDeletionModal'
import { useResearchAdjudicationDrafts } from '@/hooks/useResearchAdjudicationDrafts'
import { Loader2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'

interface GroupAdjudicationModalProps {
  isGroupModalOpen: boolean
  setIsGroupModalOpen: (open: boolean) => void
  roomId: string
  isResearchMode: boolean
}

export function GroupAdjudicationModal({
  isGroupModalOpen,
  setIsGroupModalOpen,
  roomId,
  isResearchMode,
}: GroupAdjudicationModalProps) {
  const dispatch = useAppDispatch()
  const [groupAdjudication, setGroupAdjudication] = useState('')
  const { decisions } = useAppSelector((state) => state.decisionsWS)
  const [selectedDecisions, setSelectedDecisions] = useState<Set<string>>(
    new Set()
  )
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false)
  const [isSendAllConfirmationOpen, setIsSendAllConfirmationOpen] =
    useState<boolean>(false)

  const [publicApprovals, setPublicApprovals] = useState<
    Map<string, VisibilityStatus>
  >(new Map())
  const [formattedDecisions, setFormattedDecisions] = useState<
    Map<string, string>
  >(new Map())
  const [showFormattedDecisions, setShowFormattedDecisions] = useState<
    Map<string, boolean>
  >(new Map())
  const [adjudicationByDecision, setAdjudicationByDecision] = useState<
    Map<string, string>
  >(new Map())
  const { generateDraftsForDecisionIds, isGeneratingDrafts } =
    useResearchAdjudicationDrafts(roomId)

  const allDecisionsFlat = useMemo(() => {
    if (!decisions) return []
    return [...decisions].sort((a, b) => {
      const nameA = a.user_name?.trim() || a.user || 'Anonymous'
      const nameB = b.user_name?.trim() || b.user || 'Anonymous'
      return nameA.localeCompare(nameB)
    })
  }, [decisions])

  const handleSendGroupAdjudication = () => {
    if (!groupAdjudication.trim() || selectedDecisions.size === 0) return

    const bulkAdjudicationData: BulkAdjudicationData[] = Array.from(
      selectedDecisions
    ).map((decision_id) => {
      const decision = allDecisionsFlat.find(
        (d) => String(d.decision_id) === decision_id
      )
      const isPublic = decision?.is_public
      const approvalStatus =
        publicApprovals.get(decision_id) || VisibilityStatus.PENDING_APPROVAL
      const formattedDecision = formattedDecisions.get(decision_id)

      const customAdjudication = adjudicationByDecision.get(decision_id)?.trim()
      return {
        decision_id,
        adjudication: customAdjudication || groupAdjudication,
        ...(isPublic && {
          approvalStatus,
          formattedDecision,
        }),
      }
    })

    dispatch(bulkAdjudicateDecisions({ adjudications: bulkAdjudicationData }))

    setIsGroupModalOpen(false)
    setSelectedDecisions(new Set())
    setGroupAdjudication('')
    setPublicApprovals(new Map())
    setFormattedDecisions(new Map())
    setShowFormattedDecisions(new Map())
    setAdjudicationByDecision(new Map())
  }

  const getDecisionDisplayName = (decision: StudentDecision) => {
    return decision?.user_name?.trim() || decision?.user || 'Anonymous'
  }

  const toggleDecisionSelection = useCallback(
    (decisionId: string) => {
      const newSelected = new Set(selectedDecisions)
      if (newSelected.has(decisionId)) {
        newSelected.delete(decisionId)
      } else {
        newSelected.add(decisionId)
      }
      setSelectedDecisions(newSelected)
    },
    [selectedDecisions]
  )

  const selectAllPending = useCallback(() => {
    const pendingDecisions = allDecisionsFlat.filter((d) => !d.adjudication)
    const newSelected = new Set(pendingDecisions.map((d) => String(d.decision_id)))
    setSelectedDecisions(newSelected)
  }, [allDecisionsFlat])

  const deselectAll = useCallback(() => {
    setSelectedDecisions(new Set())
    setPublicApprovals(new Map())
    setFormattedDecisions(new Map())
    setShowFormattedDecisions(new Map())
  }, [])

  const togglePublicApproval = useCallback(
    (decisionId: string, isApproved: boolean) => {
      setPublicApprovals((prev) => {
        const newMap = new Map(prev)
        newMap.set(
          decisionId,
          isApproved
            ? VisibilityStatus.APPROVED_PUBLIC
            : VisibilityStatus.PENDING_APPROVAL
        )
        return newMap
      })
    },
    []
  )

  const updateFormattedDecision = useCallback(
    (decisionId: string, formattedDecision: string) => {
      setFormattedDecisions((prev) => {
        const newMap = new Map(prev)
        newMap.set(decisionId, formattedDecision)
        return newMap
      })
    },
    []
  )

  const toggleShowFormattedDecision = useCallback((decisionId: string) => {
    setShowFormattedDecisions((prev) => {
      const newMap = new Map(prev)
      newMap.set(decisionId, !prev.get(decisionId))
      return newMap
    })
  }, [])

  const handleModalClose = useCallback(() => {
    setIsGroupModalOpen(false)
    setSelectedDecisions(new Set())
    setGroupAdjudication('')
    setPublicApprovals(new Map())
    setFormattedDecisions(new Map())
    setShowFormattedDecisions(new Map())
    setAdjudicationByDecision(new Map())
  }, [setIsGroupModalOpen])

  const latestPendingRound = useMemo(() => {
    const pendingRounds = allDecisionsFlat
      .filter((decision) => !decision.adjudication && decision.round_number != null)
      .map((decision) => decision.round_number as number)
    if (!pendingRounds.length) return null
    return Math.max(...pendingRounds)
  }, [allDecisionsFlat])

  const latestRoundPendingDecisions = useMemo(() => {
    if (latestPendingRound == null) return []
    return allDecisionsFlat.filter(
      (decision) =>
        !decision.adjudication && decision.round_number === latestPendingRound
    )
  }, [allDecisionsFlat, latestPendingRound])

  const handleGenerateForLatestRound = useCallback(async () => {
    if (!latestRoundPendingDecisions.length) {
      toast.message('No pending decisions in latest round')
      return
    }
    const drafts = await generateDraftsForDecisionIds(
      latestRoundPendingDecisions.map((decision) => decision.decision_id),
      `Generated adjudications for round ${latestPendingRound}`
    )
    // Build updates synchronously so generatedIds is fully populated
    // before the toast condition is evaluated (setState callbacks are async).
    const generatedIds = new Set<string>()
    const adjudicationUpdates = new Map<string, string>()

    drafts.forEach((draft) => {
      const decisionId = String(draft.student_decision_id)
      if (!draft.suggested_adjudication) return
      generatedIds.add(decisionId)
      adjudicationUpdates.set(decisionId, draft.suggested_adjudication)
    })

    setAdjudicationByDecision((prev) => {
      const next = new Map(prev)
      adjudicationUpdates.forEach((value, key) => next.set(key, value))
      return next
    })
    const newSelected = new Set(
      latestRoundPendingDecisions.map((decision) => String(decision.decision_id))
    )
    setSelectedDecisions(newSelected)
    const targetedCount = latestRoundPendingDecisions.length
    const generatedCount = generatedIds.size
    if (generatedCount === 0) {
      toast.error('Drafts generated but no adjudication text was returned')
    } else if (generatedCount < targetedCount) {
      toast.warning(
        `Generated text for ${generatedCount}/${targetedCount} decisions in round ${latestPendingRound}`
      )
    } else {
      toast.success(
        `Generated and filled adjudication text for all ${targetedCount} decisions in round ${latestPendingRound}`
      )
    }
  }, [
    generateDraftsForDecisionIds,
    latestPendingRound,
    latestRoundPendingDecisions,
  ])

  const handleSendToAllLatestRound = useCallback(() => {
    if (!latestRoundPendingDecisions.length) {
      toast.message('No pending decisions in latest round')
      return
    }
    const adjudications: BulkAdjudicationData[] = latestRoundPendingDecisions
      .map((decision) => {
        const adjudication =
          adjudicationByDecision.get(String(decision.decision_id))?.trim() ||
          groupAdjudication.trim()
        if (!adjudication) return null
        const approvalStatus =
          publicApprovals.get(String(decision.decision_id)) ||
          VisibilityStatus.PENDING_APPROVAL
        const formattedDecision = formattedDecisions.get(String(decision.decision_id))
        return {
          decision_id: decision.decision_id,
          adjudication,
          ...(decision.is_public && { approvalStatus, formattedDecision }),
        }
      })
      .filter((item): item is BulkAdjudicationData => item !== null)

    if (!adjudications.length) {
      toast.error('Generate or enter adjudication text before sending')
      return
    }
    dispatch(bulkAdjudicateDecisions({ adjudications }))
    handleModalClose()
  }, [
    adjudicationByDecision,
    dispatch,
    formattedDecisions,
    groupAdjudication,
    handleModalClose,
    latestRoundPendingDecisions,
    publicApprovals,
  ])

  return (
    <Dialog open={isGroupModalOpen} onOpenChange={setIsGroupModalOpen}>
      <DialogContent
        className='max-w-4xl max-h-[85vh] flex flex-col'
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <UserGroupIcon className='w-5 h-5 text-sky-600' />
            Group Adjudication
          </DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-4 flex-1 overflow-hidden'>
          <div className='flex items-center justify-between'>
            <span className='text-sm font-medium text-gray-700'>
              Selected: {selectedDecisions.size} decision(s)
            </span>
            <div className='flex gap-2 flex-wrap justify-end'>
              <Button
                variant='outline'
                size='sm'
                onClick={selectAllPending}
                className='text-xs'
              >
                Select All Pending
              </Button>
              {isResearchMode && (
                <>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => void handleGenerateForLatestRound()}
                    disabled={
                      isGeneratingDrafts || latestRoundPendingDecisions.length === 0
                    }
                    className='text-xs'
                  >
                    {isGeneratingDrafts ? (
                      <Loader2 className='w-4 h-4 mr-1 animate-spin' />
                    ) : (
                      <Sparkles className='w-4 h-4 mr-1' />
                    )}
                    Generate adjudication for all
                  </Button>
                  <Button
                    size='sm'
                    onClick={() => setIsSendAllConfirmationOpen(true)}
                    disabled={latestRoundPendingDecisions.length === 0}
                    className='text-xs bg-indigo-600 hover:bg-indigo-700 text-white'
                  >
                    Send adjudication to all
                  </Button>
                </>
              )}
              <Button
                variant='outline'
                size='sm'
                onClick={deselectAll}
                className='text-xs'
                disabled={selectedDecisions.size === 0}
              >
                Deselect All
              </Button>
            </div>
          </div>

          <div className='flex-1 overflow-y-auto border rounded-lg bg-gray-50 p-3 space-y-2'>
            {allDecisionsFlat.map((decision) => {
              const isAdjudicated = !!decision.adjudication
              const isSelected = selectedDecisions.has(String(decision.decision_id))
              const displayName = getDecisionDisplayName(decision)
              const isPublic = decision.is_public
              const isApproved =
                publicApprovals.get(String(decision.decision_id)) ===
                VisibilityStatus.APPROVED_PUBLIC
              const showFormatted =
                showFormattedDecisions.get(String(decision.decision_id)) || false
              const formattedDecision =
                formattedDecisions.get(String(decision.decision_id)) ||
                decision.formatted_decision ||
                decision.decision

              return (
                <div
                  key={decision.decision_id}
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-sky-300 bg-sky-50'
                      : 'border-gray-200 bg-white'
                  } ${!isAdjudicated ? 'cursor-pointer hover:border-sky-200' : 'opacity-60 cursor-not-allowed'}`}
                  onClick={() =>
                    !isAdjudicated &&
                    toggleDecisionSelection(String(decision.decision_id))
                  }
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={isAdjudicated}
                    onChange={() =>
                      !isAdjudicated &&
                      toggleDecisionSelection(String(decision.decision_id))
                    }
                    className='mt-1 flex-shrink-0'
                  />

                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2 mb-2 flex-wrap'>
                      <OGEAvatar
                        src={
                          decision.user_profile_picture
                            ? SERVER_URL + decision.user_profile_picture
                            : null
                        }
                        fallback={displayName.substring(0, 2).toUpperCase()}
                        variant='sm'
                        isLoading={false}
                      />
                      <div className='flex-1 min-w-0'>
                        <div className='font-semibold text-sm text-gray-900 truncate'>
                          {displayName}
                        </div>
                        {decision.round_number && (
                          <div className='text-xs text-gray-500'>
                            Round {decision.round_number}
                          </div>
                        )}
                      </div>
                      <div className='flex items-center gap-1 flex-shrink-0'>
                        {isAdjudicated && (
                          <Badge
                            variant='default'
                            className='bg-green-100 text-green-800 border-green-300'
                          >
                            Adjudicated
                          </Badge>
                        )}
                        {decision.late_submission && (
                          <Badge variant='destructive'>Late</Badge>
                        )}
                        {isPublic && (
                          <Badge
                            variant='outline'
                            className='text-blue-600 border-blue-300'
                          >
                            Public
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className='text-sm text-gray-700 bg-gray-100 rounded p-2 mb-2'>
                      <div className='flex items-center justify-between mb-1'>
                        <div className='font-medium text-xs text-gray-500'>
                          Decision:
                        </div>
                        {isPublic && isSelected && (
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleShowFormattedDecision(String(decision.decision_id))
                            }}
                            className='text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-100 h-6 px-2'
                          >
                            {showFormatted ? 'Show Original' : 'Show Modified'}
                          </Button>
                        )}
                      </div>

                      {!showFormatted ? (
                        <div className='whitespace-pre-wrap break-words'>
                          {decision.decision || 'No decision provided'}
                        </div>
                      ) : (
                        <Textarea
                          value={formattedDecision}
                          onChange={(e) => {
                            e.stopPropagation()
                            updateFormattedDecision(
                              String(decision.decision_id),
                              e.target.value
                            )
                          }}
                          placeholder='Rewrite or format the decision here...'
                          className='min-h-[80px] resize-none bg-white text-sm'
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>

                    {decision.decision_justification && (
                      <div
                        className='text-sm bg-gray-100 rounded p-2 mb-2'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className='font-medium text-xs text-gray-500 mb-1'>
                          Justification:
                        </div>
                        <div className='whitespace-pre-wrap break-words text-gray-700'>
                          {decision.decision_justification}
                        </div>
                      </div>
                    )}

                    {isPublic && isSelected && (
                      <div
                        className='flex items-start space-x-3 p-2 bg-blue-50 rounded border border-blue-200 mb-2'
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          id={`public-approval-${decision.decision_id}`}
                          checked={isApproved}
                          onChange={(e) => {
                            e.stopPropagation()
                            togglePublicApproval(
                              String(decision.decision_id),
                              e.target.checked
                            )
                          }}
                          className='mt-0.5 flex-shrink-0'
                        />
                        <div className='space-y-1 flex-1'>
                          <Label
                            htmlFor={`public-approval-${decision.decision_id}`}
                            className='text-sm font-medium text-gray-900 cursor-pointer'
                          >
                            Approve for Public Sharing
                          </Label>
                          <p className='text-xs text-gray-500'>
                            This decision can be shared with other students.
                          </p>
                        </div>
                      </div>
                    )}

                    {isAdjudicated && (
                      <div className='text-sm text-gray-700 bg-green-50 border border-green-200 rounded p-2'>
                        <div className='font-medium text-xs text-green-700 mb-1'>
                          Adjudication:
                        </div>
                        <div className='whitespace-pre-wrap break-words text-green-900'>
                          {decision.adjudication}
                        </div>
                      </div>
                    )}

                    {!isAdjudicated && isSelected && (
                      <div className='mt-2'>
                        <Label className='text-xs text-gray-600'>
                          Adjudication
                        </Label>
                        <Textarea
                          value={
                            adjudicationByDecision.get(String(decision.decision_id)) ||
                            groupAdjudication
                          }
                          onChange={(e) => {
                            e.stopPropagation()
                            setAdjudicationByDecision((prev) => {
                              const next = new Map(prev)
                              next.set(String(decision.decision_id), e.target.value)
                              return next
                            })
                          }}
                          placeholder='Enter adjudication for this decision'
                          className='min-h-[80px] resize-none bg-white text-sm mt-1'
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}

            {allDecisionsFlat.length === 0 && (
              <div className='flex flex-col items-center justify-center py-12'>
                <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
                  <PaperAirplaneIcon className='w-8 h-8 text-gray-400' />
                </div>
                <p className='text-sm font-medium text-gray-500'>
                  No decisions available
                </p>
              </div>
            )}
          </div>

          {!isResearchMode && (
            <div className='space-y-2'>
              <label className='text-sm font-medium text-gray-700'>
                Group Adjudication Message
              </label>
              <Textarea
                value={groupAdjudication}
                onChange={(e) => setGroupAdjudication(e.target.value)}
                placeholder='Enter adjudication message to send to all selected decisions...'
                className='min-h-[100px] resize-none'
                disabled={selectedDecisions.size === 0}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleModalClose}>
            Cancel
          </Button>
          {!isResearchMode && (
            <Button
              onClick={() => setIsConfirmationModalOpen(true)}
              disabled={selectedDecisions.size === 0 || !groupAdjudication.trim()}
              className='bg-sky-600 hover:bg-sky-700 text-white'
            >
              <PaperAirplaneIcon className='w-4 h-4 mr-2' />
              Send to {selectedDecisions.size} Decision(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleSendGroupAdjudication}
        title='Send Adjudication'
        description={`Are you sure you want to send adjudication to the selected players?`}
        confirmButtonText='Send'
        cancelButtonText='Cancel'
      />
      <ConfirmationModal
        isOpen={isSendAllConfirmationOpen}
        onClose={() => setIsSendAllConfirmationOpen(false)}
        onConfirm={handleSendToAllLatestRound}
        title='Send Adjudication to All'
        description={`Are you sure you want to send adjudication to all ${latestRoundPendingDecisions.length} pending decision(s) in round ${latestPendingRound}?`}
        confirmButtonText='Send'
        cancelButtonText='Cancel'
      />
    </Dialog>
  )
}
